import { useCallback } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import {
  getPaymentsBillingManagerLocations,
  useMerchants,
  getPermittedLocations,
  GetPermittedLocationsStrategy,
  useMemoizedArray,
  MerchantsData,
} from '@frontend/payments-hooks';

const getPermissions = (
  locationIds: string[],
  strategy: GetPermittedLocationsStrategy,
  merchantsData: MerchantsData
) => {
  const { paymentsBillingManagerLocations, isPaymentsBillingManager } = getPaymentsBillingManagerLocations({
    locationIds,
    strategy,
  });

  const { hasPermission: canRefund } = getPermittedLocations(locationIds, Permission.PAYMENTS_REFUNDS, { strategy });
  const { hasPermission: canExport } = getPermittedLocations(locationIds, Permission.PAYMENTS_EXPORTS, { strategy });
  const { hasPermission: canManageSurcharging } = getPermittedLocations(
    locationIds,
    Permission.PAYMENTS_MANAGE_SURCHARGING,
    {
      strategy,
    }
  );
  const canModifyWritebacks = isPaymentsBillingManager;
  const allowedPaymentsLocations = paymentsBillingManagerLocations.filter(
    (locationId) => !!merchantsData[locationId]?.hasPayments
  );

  return {
    canRefund,
    canExport,
    canModifyWritebacks,
    canManageSurcharging,
    isPaymentsBillingManager,
    allowedPaymentsLocations,
  };
};

export const useCanDoActionsMulti = (locationIds: string[], strategy: GetPermittedLocationsStrategy = 'all') => {
  const { merchantsData } = useMerchants(locationIds);

  const getCanDoActions = useCallback(
    (locationIds: string[], strategy: GetPermittedLocationsStrategy = 'all') =>
      getPermissions(locationIds, strategy, merchantsData),
    [merchantsData]
  );

  const {
    canRefund,
    canExport,
    canModifyWritebacks,
    canManageSurcharging,
    isPaymentsBillingManager,
    allowedPaymentsLocations: allowedLocations,
  } = getCanDoActions(locationIds, strategy);

  const allowedPaymentsLocations = useMemoizedArray(allowedLocations);

  return {
    canRefund,
    canExport,
    canModifyWritebacks,
    canManageSurcharging,
    isPaymentsBillingManager,
    allowedPaymentsLocations,
    getCanDoActions,
  };
};
